/* ============================================================================
 * Curated First — v1.2 (20 Aug 2026)
 * Umami Tees · Horizon 3.0 theme (BuildMyPOD)
 *
 * v1.2 — fixes the advertised colour not actually showing. See
 *        showVariantImage() below for what was wrong and why it hid.
 *
 * Pins one product into slot #1 of a collection grid, driven by the URL.
 *
 *   ?first=<product-handle>    the product to pin        (required)
 *   ?variant=<variant-id>      the colour to show        (optional)
 *
 * Also understands Meta / Facebook catalog deep links, where ?first= is a
 * full, URL-encoded product URL, e.g.
 *   ?first=https%3A%2F%2Fumamitees.com%2Fproducts%2Ffoo%3Fvariant%3D42
 *
 * Companion files:
 *   templates/product.pincard.json        the card, with the collection's
 *                                         exact block settings
 *   sections/pinned-product-card.liquid   the wrapper that template renders
 *
 * NOTE: do not switch this to Shopify's Section Rendering API (?section_id=).
 * On this theme that renders the section standalone, ignoring the block
 * settings in product.pincard.json, and silently returns a card with no
 * image and no price. Tested 19 Aug 2026.
 *
 * Safe by design: any failure at any point leaves the page exactly as it
 * would have been without this script.
 * ========================================================================= */
(() => {
  'use strict';

  const VIEW     = 'pincard';   /* templates/product.pincard.json */
  const GRID_SEL = 'ul.product-grid';
  const PENDING  = 'curated-first-pending';

  /* ---------------------------------------------------------------------
   * 1. Read the URL
   * ------------------------------------------------------------------ */
  const qp = new URLSearchParams(location.search);
  const rawFirst = (qp.get('first') || '').trim();
  if (!rawFirst) return;                       /* nothing asked of us      */

  const digitsOnly = (v) => (/^\d+$/.test(v || '') ? String(v) : '');

  let handle = '';
  let wantedVariant = '';

  /* Meta sometimes encodes the URL more than once. Decode until stable. */
  let value = rawFirst;
  for (let i = 0; i < 3 && /%[0-9A-Fa-f]{2}/.test(value); i++) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch (_) { break; }
  }

  /* Form B — a whole product URL was handed to us (Meta catalog ads). */
  if (/^https?:\/\//i.test(value) || value.indexOf('/products/') > -1) {
    try {
      const u = new URL(value, location.origin);
      const segs = u.pathname.split('/').filter(Boolean);
      const idx = segs.lastIndexOf('products');
      if (idx > -1 && segs[idx + 1]) handle = segs[idx + 1];
      wantedVariant = digitsOnly(u.searchParams.get('variant'));
    } catch (_) { /* fall through to the plain-handle reading below */ }
  }

  /* Form A — a plain handle (static ads we build by hand). */
  if (!handle) {
    handle = value.split('?')[0].split('/').filter(Boolean).pop() || '';
  }
  handle = handle.trim();
  if (!handle) return;

  /* A top-level ?variant= always wins over one buried in the Meta URL. */
  wantedVariant = digitsOnly(qp.get('variant')) || wantedVariant;

  /* ---------------------------------------------------------------------
   * 2. Hide the grid while we work.
   *
   *    The reveal is a CSS animation, not a JS timer, so the grid comes
   *    back after 1.2s even if every network call below fails or the rest
   *    of this script throws. The page can never be left blank.
   * ------------------------------------------------------------------ */
  const style = document.createElement('style');
  style.textContent =
    '.' + PENDING + ' ' + GRID_SEL +
    '{opacity:0;animation:curatedFirstReveal .01s linear 1.2s forwards}' +
    '@keyframes curatedFirstReveal{to{opacity:1}}';
  (document.head || document.documentElement).appendChild(style);
  document.documentElement.classList.add(PENDING);

  const reveal = () => document.documentElement.classList.remove(PENDING);
  setTimeout(reveal, 1600);

  /* ---------------------------------------------------------------------
   * 3. Helpers
   * ------------------------------------------------------------------ */
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const productPath = root.replace(/\/+$/, '') + '/products/' + encodeURIComponent(handle);

  const urlWith = (params) => {
    const u = new URL(productPath, location.origin);
    Object.keys(params).forEach((k) => { if (params[k]) u.searchParams.set(k, params[k]); });
    return u.pathname + u.search;
  };

  const getGrid = () => document.querySelector(GRID_SEL);

  const cardFromHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('li.product-grid__item');
  };

  const fetchCard = async (variantId) => {
    try {
      const res = await fetch(urlWith({ view: VIEW, variant: variantId }));
      if (!res.ok) return null;
      return cardFromHtml(await res.text());
    } catch (_) { return null; }
  };

  /* ---------------------------------------------------------------------
   * Show the advertised colour.
   *
   *   The card gallery ships every colour as a <slideshow-slide>, but the
   *   server always leaves slide 0 — the product's default colour —
   *   unhidden, whatever ?variant= says. On a product whose default colour
   *   happens to be the advertised one this looks correct, which is exactly
   *   how it hid during the first round of testing. It isn't.
   *
   *   So pick the slide whose image is the variant's own, unhide that one
   *   and hide the rest. If nothing matches, leave the card untouched
   *   rather than guess.
   * ------------------------------------------------------------------ */
  const fileName = (url) => String(url || '').split('/').pop().split('?')[0];

  const showVariantImage = (card, data, variantId) => {
    if (!card || !data || !variantId) return;

    const variant = (data.variants || []).find((v) => String(v.id) === String(variantId));
    const wanted = variant && variant.featured_image ? fileName(variant.featured_image.src) : '';
    if (!wanted) return;

    const slides = card.querySelectorAll('slideshow-slide');
    if (!slides.length) return;

    let target = null;
    slides.forEach((slide) => {
      if (target) return;
      const img = slide.querySelector('img');
      if (img && fileName(img.getAttribute('src')) === wanted) target = slide;
    });
    if (!target) return;

    slides.forEach((slide) => { if (slide !== target) slide.setAttribute('hidden', ''); });
    target.removeAttribute('hidden');

    const parent = target.parentNode;
    if (parent && parent.firstChild !== target) parent.insertBefore(target, parent.firstChild);

    /* The theme ships hidden slides as loading="lazy" and only the default
       slide as eager. A lazily-loaded image that was hidden when the browser
       first saw it never loads once we unhide it — the tile renders blank.
       Promote it to eager before the card is ever attached. */
    const img = target.querySelector('img');
    if (img) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
    }
  };

  /* ---------------------------------------------------------------------
   * 4. Work out which variant we can honestly show.
   *
   *    Guards against pinning a colour that exists in Shopify but is not
   *    actually buyable (an unclaimed Printify variant renders sold out).
   *    Preference order: the exact variant asked for  ->  same colour in
   *    another size  ->  any available variant  ->  no variant at all.
   * ------------------------------------------------------------------ */
  const resolveVariant = (data) => {
    if (!data || !Array.isArray(data.variants) || !wantedVariant) return wantedVariant;

    const asked = data.variants.find((v) => String(v.id) === wantedVariant);
    if (asked && asked.available) return wantedVariant;

    const names = (data.options || []).map((o) => (typeof o === 'string' ? o : (o && o.name) || ''));
    const colourIdx = names.findIndex((n) => /colou?r/i.test(n));
    const key = colourIdx > -1 ? 'option' + (colourIdx + 1) : null;

    let fallback = null;
    if (asked && key) {
      fallback = data.variants.find((v) => v.available && v[key] === asked[key]);
    }
    if (!fallback) fallback = data.variants.find((v) => v.available);

    if (fallback) {
      console.info('[curated-first] variant ' + wantedVariant +
                   ' is unavailable, showing ' + fallback.id + ' instead');
      return String(fallback.id);
    }
    return '';   /* nothing buyable — let the card use its own default */
  };

  /* ---------------------------------------------------------------------
   * 5. Put it in place
   * ------------------------------------------------------------------ */
  let cachedCard = null;
  let productId = '';
  let applying = false;

  const shouldPin = () => {
    const s = new URLSearchParams(location.search);
    /* Don't fight the shopper: stand down once they filter or paginate. */
    for (const key of s.keys()) if (key.indexOf('filter.') === 0) return false;
    const page = s.get('page');
    return !page || page === '1';
  };

  const apply = () => {
    if (applying || !cachedCard) return;
    const grid = getGrid();
    if (!grid) return;

    const first = grid.firstElementChild;
    if (first && first.dataset && first.dataset.curatedFirst === 'true') return;
    if (!shouldPin()) { reveal(); return; }

    applying = true;
    try {
      /* Remove the product's ordinary tile so it isn't shown twice. */
      if (productId) {
        grid.querySelectorAll('li[data-product-id="' + productId + '"]')
            .forEach((li) => li.remove());
      }
      grid.prepend(cachedCard.cloneNode(true));
    } catch (err) {
      console.warn('[curated-first]', err);
    } finally {
      applying = false;
      reveal();
    }
  };

  /* ---------------------------------------------------------------------
   * 6. Go
   * ------------------------------------------------------------------ */
  (async () => {
    try {
      /* Both requests go out together; the card render is the slow one. */
      const dataPromise = fetch(productPath + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      const cardPromise = fetchCard(wantedVariant);

      const results = await Promise.all([dataPromise, cardPromise]);
      const data = results[0];
      let card = results[1];

      if (!data) { reveal(); return; }            /* bad handle — do nothing */
      productId = String(data.id || '');

      /* Only pay for a second render if the requested colour is unavailable. */
      const chosenVariant = resolveVariant(data);
      if (chosenVariant !== wantedVariant) card = await fetchCard(chosenVariant);
      if (!card) { reveal(); return; }

      card.dataset.curatedFirst = 'true';
      card.dataset.productId = productId;

      /* Put the advertised colour on screen, not the product's default. */
      showVariantImage(card, data, chosenVariant);

      /* Belt and braces: make the click land on the promised colour. */
      if (chosenVariant) {
        card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
          try {
            const u = new URL(a.getAttribute('href'), location.origin);
            u.searchParams.set('variant', chosenVariant);
            a.setAttribute('href', u.pathname + u.search);
          } catch (_) { /* leave the link alone */ }
        });
      }

      cachedCard = card;

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', apply, { once: true });
      } else {
        apply();
      }

      /* Horizon redraws the grid over AJAX when someone sorts or pages.
         Put the pin back whenever that happens. */
      const host = document.querySelector('results-list') || document.body;
      new MutationObserver(() => { if (!applying) apply(); })
        .observe(host, { childList: true, subtree: true });
    } catch (err) {
      console.warn('[curated-first]', err);
      reveal();
    }
  })();
})();
